import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DrawerModule } from 'primeng/drawer';
import { ToastrService } from 'ngx-toastr';
import type { ChargeDTO } from '../../core/models';
import { SaleService, type SaleDTO } from '../../services/sale.service';
import { SaleReadComponent } from '../sales/sale-read.component';
import {
  ForecastSalesOperationsService,
  getSalesOpsRange,
  type SalesOpsPeriod,
  type SalesOpsPeriodType,
  type SalesOperationsData,
  type SalesOpsOverdueRow
} from '../../services/forecast-sales-operations.service';

@Component({
  selector: 'app-sales-operations-dashboard',
  templateUrl: './sales-operations-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, RouterLink, ChartModule, TableModule, TagModule, DrawerModule, SaleReadComponent],
  styles: [
    `
      :host { display: block; width: 100%; }
      :host ::ng-deep .p-chart { display: block; width: 100%; height: 100%; }
      :host ::ng-deep .p-chart canvas { max-width: 100% !important; }
      :host ::ng-deep .sale-details-drawer .p-drawer-header {
        padding: 1rem 1.25rem;
        border-bottom: 1px solid rgb(226 232 240);
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-content {
        padding: 1rem 1.25rem 1.5rem;
      }
      :host ::ng-deep .sales-ops-overdue-table .p-datatable-tbody > tr {
        cursor: pointer;
      }
      :host ::ng-deep .sales-ops-overdue-table .p-datatable-tbody > tr:hover {
        background: rgb(248 250 252);
      }
      :host ::ng-deep .sales-ops-overdue-table .p-datatable-tbody > tr.row-no-sale {
        cursor: default;
        opacity: 0.65;
      }
      :host ::ng-deep .sales-ops-overdue-table .p-datatable-tbody > tr.row-no-sale:hover {
        background: transparent;
      }
    `
  ]
})
export class SalesOperationsDashboardComponent implements OnInit {
  private service = inject(ForecastSalesOperationsService);
  private saleService = inject(SaleService);
  private toastr = inject(ToastrService);

  data = signal<SalesOperationsData | null>(null);
  period = signal<SalesOpsPeriod>({ type: '30d' });
  customDateFrom = signal('');
  customDateTo = signal('');

  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  isSaleDrawerOpen = signal(false);
  saleDetailsLoading = signal(false);

  readonly overduePageSize = 10;
  readonly overduePageSizeOptions = [5, 10, 20, 50];

  readonly periodOptions: { value: SalesOpsPeriodType; label: string }[] = [
    { value: 'today', label: 'Hoy' },
    { value: '30d', label: '30 días' },
    { value: 'quarter', label: 'Trimestre' },
    { value: 'custom', label: 'Personalizado' }
  ];

  readonly donutData = computed(() => {
    const r = this.data()?.receipts;
    if (!r || r.total <= 0) return null;
    return {
      labels: ['Cobrados', 'Pendientes', 'Vencidos'],
      datasets: [{
        data: [r.paid, r.pending, r.overdue],
        backgroundColor: ['#059669', '#d97706', '#dc2626'],
        borderWidth: 0
      }]
    };
  });

  readonly donutOptions = {
    cutout: '68%',
    plugins: { legend: { display: false } },
    maintainAspectRatio: false
  };

  readonly trendData = computed(() => {
    const trend = this.data()?.salesTrend ?? [];
    return {
      labels: trend.map((w) => w.label),
      datasets: [
        {
          label: 'Ventas',
          data: trend.map((w) => w.salesCount),
          borderColor: '#4f46e5',
          backgroundColor: 'transparent',
          tension: 0.35,
          pointRadius: 3
        },
        {
          label: 'Facturadas',
          data: trend.map((w) => w.invoicedCount),
          borderColor: '#059669',
          backgroundColor: 'transparent',
          tension: 0.35,
          pointRadius: 3
        }
      ]
    };
  });

  readonly trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: { boxWidth: 10, font: { size: 11 } }
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 10 } } }
    }
  };

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.data.update((d) => ({
      ...(d ?? this.emptyShell()),
      loading: true,
      error: undefined
    }));
    this.service.load(this.period()).subscribe((d) => this.data.set(d));
  }

  setPeriod(type: SalesOpsPeriodType): void {
    if (type === 'custom') {
      const { from, to } = getSalesOpsRange({ type: '30d' });
      this.customDateFrom.set(toDayKey(from));
      this.customDateTo.set(toDayKey(to));
      this.period.set({ type: 'custom' });
      return;
    }
    this.period.set({ type });
    this.load();
  }

  applyCustomPeriod(): void {
    const from = this.customDateFrom();
    const to = this.customDateTo();
    if (!from || !to) return;
    this.period.set({ type: 'custom', dateFrom: from, dateTo: to });
    this.load();
  }

  periodLabel(): string {
    const { from, to } = getSalesOpsRange(this.period());
    const fmt = (d: Date) =>
      d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${fmt(from)} – ${fmt(to)}`;
  }

  updatedLabel(): string {
    const raw = this.data()?.updatedAt;
    if (!raw) {
      return `Actualizado ${new Date().toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit' })} h`;
    }
    const dt = new Date(raw);
    const isToday = dt.toDateString() === new Date().toDateString();
    const time = dt.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    return isToday ? `Actualizado hoy, ${time} h` : `Actualizado ${dt.toLocaleString('es-MX')}`;
  }

  processDotClass(tone: string): string {
    if (tone === 'warning') return 'bg-amber-500';
    if (tone === 'healthy') return 'bg-emerald-500';
    return 'bg-slate-400';
  }

  openSaleFromRow(row: SalesOpsOverdueRow): void {
    if (!row.saleId) {
      this.toastr.info('Este recibo no tiene una venta asociada');
      return;
    }
    this.isSaleDrawerOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(row.saleId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          if (detail.id != null) {
            this.saleService.getSaleCharges(detail.id).subscribe({
              next: (chargesResp) => this.saleCharges.set(chargesResp.body ?? []),
              error: () => this.saleCharges.set([])
            });
          }
        } else {
          this.toastr.error(response.message || 'No se pudo cargar la venta');
          this.closeSaleDrawer();
        }
        this.saleDetailsLoading.set(false);
      },
      error: () => {
        this.saleDetailsLoading.set(false);
        this.toastr.error('Error al cargar la venta');
        this.closeSaleDrawer();
      }
    });
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleDrawer();
  }

  closeSaleDrawer(): void {
    this.isSaleDrawerOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  private emptyShell(): SalesOperationsData {
    return {
      from: '', to: '',
      alert: { overdueCount: 0, overdueWithReminderCount: 0, minDaysOverdue: 0 },
      receipts: { total: 0, paid: 0, pending: 0, overdue: 0, paidPercent: 0, pendingPercent: 0, overduePercent: 0 },
      sales: { total: 0, subscriptionCount: 0, manualCount: 0, invoiced: 0, pendingInvoice: 0, invoicedPercent: 0 },
      salesTrend: [], recentOverdue: [], processes: [], loading: false
    };
  }
}

function toDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
